// Renders a blog body written in the editor's light Markdown syntax.
// Outbound links carry rel="nofollow ugc noopener noreferrer" (site-wide spam
// policy) and eBay links are automatically EPN-tagged so affiliate revenue is
// attributed even when the author pastes a plain eBay URL.
import { Link } from "@tanstack/react-router";
import { parseBlocks, parseInline, type Span } from "@/lib/blog-shared";
import { ebayAffiliateUrl } from "@/lib/ebay-affiliate";

function Inline({ text }: { text: string }) {
  return (
    <>
      {parseInline(text).map((s, i) => (
        <SpanNode key={i} span={s} />
      ))}
    </>
  );
}

function SpanNode({ span }: { span: Span }) {
  switch (span.kind) {
    case "strong":
      return <strong className="font-semibold text-foreground">{span.text}</strong>;
    case "em":
      return <em>{span.text}</em>;
    case "code":
      return <code className="rounded bg-card px-1.5 py-0.5 text-[0.9em]">{span.text}</code>;
    case "link": {
      const href = span.href;
      if (href.startsWith("/")) {
        return (
          <Link to={href as never} className="font-semibold text-primary hover:underline">
            {span.text}
          </Link>
        );
      }
      return (
        <a
          href={ebayAffiliateUrl(href, "blog")}
          target="_blank"
          rel="nofollow ugc noopener noreferrer"
          className="font-semibold text-primary hover:underline"
        >
          {span.text}
        </a>
      );
    }
    default:
      return <>{span.text}</>;
  }
}

export function BlogBody({ body, hideSs01CardImages = false }: { body: string; hideSs01CardImages?: boolean }) {
  const blocks = parseBlocks(body);
  return (
    <div className="space-y-5">
      {blocks.map((b, i) => {
        switch (b.kind) {
          case "h2":
            return (
              <h2 key={i} className="mt-8 text-2xl font-bold">
                {b.text}
              </h2>
            );
          case "h3":
            return (
              <h3 key={i} className="mt-6 text-lg font-bold">
                {b.text}
              </h3>
            );
          case "p":
            return (
              <p key={i} className={`${i === 0 ? "aeo-answer " : ""}text-[15px] leading-relaxed text-muted-foreground`}>
                <Inline text={b.text} />
              </p>
            );
          case "quote":
            return (
              <blockquote key={i} className="border-l-2 border-primary/60 pl-4 text-[15px] italic text-muted-foreground">
                <Inline text={b.text} />
              </blockquote>
            );
          case "ul":
            return (
              <ul key={i} className="list-disc space-y-1.5 pl-5 text-[15px] text-muted-foreground">
                {b.items.map((it, j) => (
                  <li key={j}>
                    <Inline text={it} />
                  </li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={i} className="list-decimal space-y-1.5 pl-5 text-[15px] text-muted-foreground">
                {b.items.map((it, j) => (
                  <li key={j}>
                    <Inline text={it} />
                  </li>
                ))}
              </ol>
            );
          case "img":
            if (
              hideSs01CardImages &&
              (b.src.includes("/ss01-card-") || b.src.includes("/ss01-slide"))
            ) return null;
            return (
              <figure key={i} className="overflow-hidden rounded-2xl border border-border/60">
                <img src={b.src} alt={b.alt} loading="lazy" decoding="async" className="w-full object-cover" />
                {b.alt ? <figcaption className="px-3 py-2 text-xs text-muted-foreground">{b.alt}</figcaption> : null}
              </figure>
            );
          case "youtube":
            return (
              <div key={i} className="aspect-video overflow-hidden rounded-2xl border border-border/60">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${b.id}`}
                  title="Embedded video"
                  loading="lazy"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
            );
          case "hr":
            return <hr key={i} className="border-border/60" />;
          default:
            return null;
        }
      })}
    </div>
  );
}
